<template lang="pug">
.border.border-gray-300.dark_border-gray-700.rounded-md.mt-3(class="p-2.5")
  .rounded.px-2.text-sm.font-medium.dark_text-white(
    class="py-1.5" 
    :class='{ "bg-indigo-50 dark_bg-indigo-500 text-indigo-800 ": status === "accepted" || status === "mixed", "bg-gray-100 dark_bg-gray-500 text-gray-700": status === "rejected", "bg-orange-100 dark_bg-amber-700 text-orange-800": status === "pending" }'
  )
    .flex.items-center.gap-2
      span.text-center.flex-1 {{ statusBannerText }}
      .flex.items-center.gap-1(v-if="showPendingMultiNavigator")
        button.lite-nav-arrow.p-1.rounded.transition-colors.disabled_opacity-40.disabled_cursor-not-allowed(
          type="button"
          :disabled="props.disableActions || !canGoPrev"
          :class='[{ "text-indigo-700 dark_text-indigo-100 hover_text-indigo-900": status === "accepted", "text-gray-600 dark_text-gray-200 hover_text-gray-800": status === "rejected", "text-orange-700 dark_text-orange-100 hover_text-orange-900": status === "pending" || status === "mixed" }, { "invisible pointer-events-none": !canGoPrev }]'
          @click="$emit('prev')"
          aria-label="Previous change"
        )
          Icon(icon="ph:caret-up")
        div(class="text-[10px] leading-none text-center min-w-6")
          span.font-bold(:class='{ "text-indigo-800 dark_text-indigo-50": status === "accepted", "text-gray-700 dark_text-gray-50": status === "rejected", "text-orange-800 dark_text-orange-50": status === "pending" || status === "mixed" }') {{ props.total > 0 ? props.index + 1 : 0 }}
          span.opacity-75(:class='{ "text-indigo-700 dark_text-indigo-100": status === "accepted", "text-gray-600 dark_text-gray-200": status === "rejected", "text-orange-700 dark_text-orange-100": status === "pending" || status === "mixed" }')  / {{ props.total }}
        button.lite-nav-arrow.p-1.rounded.transition-colors.disabled_opacity-40.disabled_cursor-not-allowed(
          type="button"
          :disabled="props.disableActions || !canGoNext"
          :class='[{ "text-indigo-700 dark_text-indigo-100 hover_text-indigo-900": status === "accepted", "text-gray-600 dark_text-gray-200 hover_text-gray-800": status === "rejected", "text-orange-700 dark_text-orange-100 hover_text-orange-900": status === "pending" || status === "mixed" }, { "invisible pointer-events-none": !canGoNext }]'
          @click="$emit('next')"
          aria-label="Next change"
        )
          Icon(icon="ph:caret-down")

  .mt-2.pt-1.flex.items-center.gap-2(class="min-h-[36px]")
    .flex-1.min-w-0
      label.label(
        v-if="canView && !isFinal && isValid"
        class="flex items-center gap-2 select-none text-gray-700 dark_text-gray-200"
      )
        input.input(
          type="checkbox"
          :checked="showPreviousVersion"
          title="Show previous version for all screens"
          @change="onShowPreviousVersionChange"
          :id="`show-previous-${props.messageId}`"
        )
        span.label-text Show previous version
      span.text-xs.text-amber-600.dark_text-amber-400(
        v-else-if="canView && !isFinal && !isValid"
      ) Error applying edit

    template(v-if="props.status === 'pending' && !isFinal")
      button.inline-flex.items-center.gap-1.text-slate-400.dark_text-slate-300.hover_text-slate-600.dark_hover_text-slate-100.transition-colors.disabled_opacity-40.disabled_cursor-not-allowed(
        type="button"
        :disabled="props.disableActions"
        @click="$emit('reject-current')"
      )
        Icon(icon="qlementine-icons:close-16" class="text-sm")
        span.text-sm.font-medium Reject

      button.inline-flex.items-center.gap-1.text-slate-500.dark_text-slate-200.hover_text-slate-700.dark_hover_text-white.transition-colors.disabled_opacity-40.disabled_cursor-not-allowed(
        type="button"
        :disabled="props.disableActions || !props.isValid"
        @click="$emit('accept-current')"
      )
        Icon(icon="ph:check" class="text-sm")
        span.text-sm.font-medium Accept

    template(v-else-if="props.status === 'pending' && isFinal")
      .inline-flex.items-center.text-sm.font-medium.text-slate-700.dark_text-slate-100(
        v-if="props.currentDecision === 'rejected'"
      )
        span Rejected
        Icon(icon="ph:x-circle" class="ps-1 text-2xl")
      .inline-flex.items-center.text-sm.font-medium.text-slate-700.dark_text-slate-100(
        v-if="props.currentDecision === 'accepted'"
      )
        span Accepted
        Icon(icon="ph:check-circle" class="ps-1 text-2xl")

    template(v-else)
      .ms-auto.flex.items-center.gap-2
        button.inline-flex.items-center.gap-1.text-slate-500.dark_text-slate-200.hover_text-slate-700.dark_hover_text-white.transition-colors(
          type="button"
          @click="onRevertClick"
        )
          Icon(icon="ph:arrow-counter-clockwise" class="text-xl")
          span.text-sm.font-medium Revert
        button.inline-flex.items-center.gap-1.text-slate-500.dark_text-slate-200.hover_text-slate-700.dark_hover_text-white.transition-colors(
          v-if="!isMultiChange"
          type="button"
          @click="emit('view-current')"
        )
          Icon(icon="ph:cards-three" class="text-xl")
          span.text-sm.font-medium View

  //- Revert confirmation when there are newer messages (Figma: Course-Editor node 3007-11101)
  Dialog.revert-dialog(
    :open="showRevertDialog"
    @close="showRevertDialog = false"
  )
    .fixed.inset-0.bg-black.bg-opacity-25.dark_bg-opacity-40(aria-hidden="true")
    .fixed.inset-0.overflow-y-auto
      .flex.min-h-full.items-center.justify-center.p-4
        DialogPanel.revert-dialog-panel(
          class="w-full max-w-[592px] rounded-xl bg-white dark_bg-gray-800 shadow-xl flex flex-col gap-5 p-6 pt-6 text-left"
        )
          p(class="text-base leading-5 text-gray-900 dark_text-gray-100")
            span.font-semibold Are you sure you want to revert this edit?
            br
            br
            span(class="font-normal text-sm leading-5 text-gray-700 dark_text-gray-300")
              | The course will load the version prior to this edit and you will lose any edits you made until that point.
              template(v-if="props.hasNewerMessages")
                |  Any edits in newer messages will also be rejected.
          .flex.justify-end.w-full(class="gap-2.5")
            button.revert-dialog-cancel(
              type="button"
              class="inline-flex items-center justify-center gap-2 rounded-lg border border-indigo-500 px-3 py-1.5 text-sm font-medium text-indigo-500 dark_text-indigo-400 dark_border-indigo-400 hover_bg-indigo-50 dark_hover_bg-indigo-900/20 transition-colors"
              @click="showRevertDialog = false"
            )
              | Cancel
            button.revert-dialog-confirm(
              type="button"
              class="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-sm font-medium text-white hover_bg-indigo-500 dark_bg-indigo-600 dark_hover_bg-indigo-500 transition-colors"
              @click="confirmRevert"
            )
              | Revert this edit

  template(v-if="isMultiChange && props.status === 'pending'")
    .mt-2.border-t.border-gray-200.dark_border-gray-700.pt-2.flex.items-center.justify-between.gap-2
      .flex.items-center.gap-4.text-sm
        button.inline-flex.items-center.text-slate-500.dark_text-slate-200.hover_text-slate-700.dark_hover_text-white.transition-colors.disabled_opacity-40.disabled_cursor-not-allowed(
          type="button"
          :disabled="props.disableActions"
          @click="$emit('accept-all')"
        ) Accept all
        button.inline-flex.items-center.text-slate-500.dark_text-slate-200.hover_text-slate-700.dark_hover_text-white.transition-colors.disabled_opacity-40.disabled_cursor-not-allowed(
          type="button"
          :disabled="props.disableActions"
          @click="$emit('reject-all')"
        ) Reject all
      .w-px.self-stretch.bg-gray-200.dark_bg-gray-700
      button.inline-flex.items-center.text-indigo-500.dark_text-indigo-400.hover_text-indigo-600.dark_hover_text-indigo-300.transition-colors.disabled_opacity-40.disabled_cursor-not-allowed.text-sm.font-medium(
        type="button"
        :disabled="props.disableActions"
        @click="$emit('done-reviewing')"
      ) I'm done reviewing
</template>

<script setup lang="ts">
import { Dialog, DialogPanel } from '@headlessui/vue';
import { Icon } from '@iconify/vue';
import { computed, ref } from 'vue';

const showRevertDialog = ref(false);

function onRevertClick() {
  if (props.hasNewerMessages) {
    showRevertDialog.value = true;
  } else {
    emit('revert-all');
  }
}

function confirmRevert() {
  showRevertDialog.value = false;
  emit('revert-all-including-newer');
}

type LiteReviewStatus = 'pending' | 'accepted' | 'rejected' | 'mixed';

const props = withDefaults(
  defineProps<{
    status?: LiteReviewStatus;
    index: number;
    messageId: string;
    total: number;
    disableActions?: boolean;
    /** When true, Revert shows a confirmation; on confirm, emit revert-all-including-newer so parent can remove newer messages too */
    hasNewerMessages?: boolean;
    /** Whether the current selection has a viewable screen (for "Preview changes" visibility) */
    canView?: boolean;
    /** Whether the current patch is finalized (accepted/rejected); hide checkbox when true */
    isFinal?: boolean;
    /** Whether the current patch YAML is valid; disable checkbox when false */
    isValid?: boolean;
    /** Decision state for currently selected screen in pending mode. */
    currentDecision?: 'accepted' | 'rejected' | 'mixed' | null;
    /** Global "Show previous version" toggle: true = show previous for all screens, false = show new changes. */
    showPreviousVersion?: boolean;
  }>(),
  {
    status: 'pending',
    disableActions: false,
    hasNewerMessages: false,
    canView: false,
    isFinal: true,
    isValid: false,
    currentDecision: null,
    showPreviousVersion: false
  }
);

const emit = defineEmits<{
  (e: 'prev'): void;
  (e: 'next'): void;
  (e: 'accept-current'): void;
  (e: 'reject-current'): void;
  (e: 'accept-all'): void;
  (e: 'reject-all'): void;
  (e: 'done-reviewing'): void;
  (e: 'revert-all'): void;
  (e: 'revert-all-including-newer'): void;
  (e: 'update:showPreviousVersion', value: boolean): void;
  (e: 'view-current'): void;
}>();

function onShowPreviousVersionChange(e: Event) {
  const target = e.target as HTMLInputElement;
  emit('update:showPreviousVersion', target?.checked ?? false);
}

const isSingleChange = computed(() => props.total <= 1);
const isMultiChange = computed(() => props.total > 1);
const showPendingMultiNavigator = computed(
  () => isMultiChange.value && props.status === 'pending'
);
const canGoPrev = computed(() => props.index > 0);
const canGoNext = computed(() => props.index < props.total - 1);

const statusBannerText = computed(() => {
  if (props.status === 'accepted') {
    return isSingleChange.value
      ? 'Edit made successfully'
      : 'Edits made successfully';
  }
  if (props.status === 'rejected') {
    return isSingleChange.value
      ? 'Proposed edit rejected'
      : 'Proposed edits rejected';
  }
  if (props.status === 'mixed')
    return 'Some proposed edits accepted and rejected';
  return isSingleChange.value
    ? 'Please review the proposed edit'
    : 'Please review the proposed edits';
});
</script>

<style scoped>
@media (max-width: 767px) and (orientation: portrait) {
  .lite-nav-arrow {
    display: none;
  }
}
</style>
